import * as React from "react";
import { Radio, RenderLayer, Toggle } from "react-ui";
import ReactDOM from "react-dom";
import { ColumnStack, Fit } from "@skbkontur/react-stack-layout";

import { CaptionsViewMode, HintsViewMode } from "../ViewModes";
import { emptyRef } from "../../../TypingUtils";
import { DomElementHack } from "../../../Commons/DOM/DomElementHack";
import { toHtmlElementOrNull } from "../../../Commons/DOM/DomHelpers";
import { useMetrics } from "../../Metrics/MetricsProviderContext";
import { VIEWMODE_CHANGE_CAPTIONS, VIEWMODE_CHANGE_HINTS, VIEWMODE_SELECTOR_OPEN } from "../../../metricsAction";

import { Header2, Panel, PanelPositionWrapper, RadioGroupLine, SecondaryText } from "./ViewModeSelectButton.styles";
import { RenderContainer } from "./Portals";

export interface ViewModeSelectButtonProps {
    captionsViewMode: CaptionsViewMode;
    hintsViewMode: HintsViewMode;
    onChangeCaptionsViewMode: (next: CaptionsViewMode) => void;
    onChangeHintsViewMode: (next: HintsViewMode) => void;
    children: (onClick: () => void, opened: boolean, menu: React.ReactNode) => React.ReactNode;
}

export function ViewModeSelectButton(props: ViewModeSelectButtonProps): JSX.Element {
    const track = useMetrics();
    const [opened, setOpened] = React.useState(false);
    const targetElement = React.useRef<DomElementHack>(emptyRef);
    const [position, setPosition] = React.useState<[number, number]>([0, 0]);

    const handleClick = () => {
        if (!opened) {
            track(VIEWMODE_SELECTOR_OPEN);
            if (targetElement.current) {
                const targetDomElement = toHtmlElementOrNull(ReactDOM.findDOMNode(targetElement.current));
                const targetRect = targetDomElement?.getBoundingClientRect();
                if (targetRect) {
                    setPosition([targetRect.left, targetRect.top]);
                }
            }
        }
        setOpened(!opened);
    };

    const menu = (
        <RenderContainer>
            <PanelPositionWrapper position={position}>
                <Panel opened={opened} onClick={e => e.stopPropagation()}>
                    <Header2>Настройка оформления</Header2>
                    <ColumnStack block stretch gap={5}>
                        <Fit>
                            Названия полей
                            <RadioGroupLine>
                                <Radio
                                    value={"Document"}
                                    checked={props.captionsViewMode === CaptionsViewMode.Long}
                                    onClick={() => {
                                        track(VIEWMODE_CHANGE_CAPTIONS, { value: "long" });
                                        props.onChangeCaptionsViewMode(CaptionsViewMode.Long);
                                    }}>
                                    <div>
                                        Полные <br />
                                        <SecondaryText>как в печатной форме</SecondaryText>
                                    </div>
                                </Radio>
                            </RadioGroupLine>
                            <RadioGroupLine>
                                <Radio
                                    value={"Scroll"}
                                    checked={props.captionsViewMode === CaptionsViewMode.Short}
                                    onClick={() => {
                                        track(VIEWMODE_CHANGE_CAPTIONS, { value: "short" });
                                        props.onChangeCaptionsViewMode(CaptionsViewMode.Short);
                                    }}>
                                    Краткие
                                </Radio>
                            </RadioGroupLine>
                        </Fit>
                        <Fit>
                            <Toggle
                                checked={props.hintsViewMode === HintsViewMode.Show}
                                onValueChange={nextValue => {
                                    track(VIEWMODE_CHANGE_HINTS, { value: nextValue ? "show" : "hide" });
                                    props.onChangeHintsViewMode(nextValue ? HintsViewMode.Show : HintsViewMode.Hide);
                                }}>
                                Показывать подсказки для заполнения
                            </Toggle>
                        </Fit>
                    </ColumnStack>
                </Panel>
            </PanelPositionWrapper>
        </RenderContainer>
    );

    const handleClickOutside = () => {
        setOpened(false);
    };

    return (
        <RenderLayer onClickOutside={handleClickOutside}>
            <DomElementHack ref={targetElement}>{props.children(handleClick, opened, menu)}</DomElementHack>
        </RenderLayer>
    );
}
